import React, { useState, useEffect } from 'react';
import { Modal, Button } from "react-bootstrap";
import Swal from 'sweetalert2';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../api/api';

const API_branch = `${API_URL}/branches`;

const ManageBranchLayer = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    const [branches, setBranches] = useState([]);
    const [filteredBranches, setFilteredBranches] = useState([]);
    const [loading, setLoading] = useState(true);

    // Search and Filter
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        fetchBranches();
    }, []);

    useEffect(() => {
        applySearchAndFilter();
    }, [searchTerm, statusFilter, branches]);

    const fetchBranches = async () => {
        setLoading(true);
        try {
            const res = await axios.get(API_branch, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBranches(res.data);
        } catch (error) {
            console.error("Error fetching branches:", error);
            Swal.fire("Error", "Failed to fetch branches", "error");
        } finally {
            setLoading(false);
        }
    };

    const applySearchAndFilter = () => {
        let result = [...branches];

        if (searchTerm.trim() !== '') {
            const lowerSearch = searchTerm.toLowerCase();
            result = result.filter(branch =>
                branch.branchname.toLowerCase().includes(lowerSearch) ||
                branch.branchcode.toLowerCase().includes(lowerSearch) ||
                (branch.collectionday?.toLowerCase() || '').includes(lowerSearch)
            );
        }

        if (statusFilter !== 'all') {
            result = result.filter(branch =>
                (branch.status || '').toLowerCase() === statusFilter
            );
        }

        setFilteredBranches(result);
        setCurrentPage(1); // reset to first page on search/filter
    };


    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: 'Are you sure?',
            text: "This branch will be deleted permanently!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it!'
        });

        if (confirm.isConfirmed) {
            try {
                await axios.delete(`${API_branch}/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Deleted!', 'Branch has been deleted.', 'success');
                fetchBranches();
            } catch (error) {
                console.error("Delete failed:", error);
                Swal.fire('Error', 'Failed to delete branch.', 'error');
            }
        }
    };

    const toggleStatus = async (branch) => {
        const newStatus = branch.status.toLowerCase() === 'inactive' ? 'active' : 'inactive';

        try {
            await axios.put(
                `${API_branch}/${branch.id}`,
                { status: newStatus },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setBranches(prev =>
                prev.map(b => b.id === branch.id ? { ...b, status: newStatus } : b)
            );
            Swal.fire('Success', `Status changed to ${newStatus}`, 'success');
        } catch (error) {
            console.error("Failed to update status:", error);
            Swal.fire('Error', 'Failed to update status', 'error');
        }
    };

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredBranches.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredBranches.length / itemsPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div className="row gy-4">
            <div className="col-md-12">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h6 className="mb-0">Branches</h6>
                        <div className="d-flex gap-2 flex-wrap">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search by name/code"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <select
                                className="form-select form-select-sm"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                            <Link to="/addbranch" className="btn btn-sm btn-primary">
                                <i className="ri-add-line" /> Add Branch
                            </Link>
                        </div>
                    </div>

                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center p-4">Loading branches...</div>
                        ) : (
                            <>
                                <table className="table table-bordered mb-0">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Name / Code</th>
                                            <th>Coll. Day</th>
                                            <th>Mobile</th>
                                            <th>Opening Date</th>
                                            <th>Status</th>
                                            <th style={{ width: '120px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center">No branches found.</td>
                                            </tr>
                                        ) : (
                                            currentItems.map(branch => (
                                                <tr key={branch.id}>
                                                    <td>{branch.id}</td>
                                                    <td>{branch.branchname} / {branch.branchcode}</td>
                                                    <td>{branch.collectionday}</td>
                                                    <td>{branch.contactnumber}</td>
                                                    <td>{branch.openingdate}</td>
                                                    <td>
                                                        <button
                                                            className={`btn btn-sm ${branch.status?.toLowerCase() === 'inactive' ? 'btn-warning' : 'btn-success'}`}
                                                            onClick={() => toggleStatus(branch)}
                                                            style={{ minWidth: '90px' }}
                                                        >
                                                            {branch.status ? branch.status.charAt(0).toUpperCase() + branch.status.slice(1).toLowerCase() : 'Active'}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-info me-2"
                                                            onClick={() => navigate(`/editbranch/${branch.id}`)}
                                                        >
                                                            <i className="ri-edit-line" />
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDelete(branch.id)}
                                                        >
                                                            <i className="ri-delete-bin-line" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="p-3 d-flex justify-content-center">
                                        <ul className="pagination pagination-sm mb-0">
                                            {Array.from({ length: totalPages }, (_, i) => (
                                                <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(i + 1)}
                                                    >
                                                        {i + 1}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManageBranchLayer;
